import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { admin, fileUrl } from "../../lib/api";
import { slugify } from "../../lib/format";
import { panel, inputCls, btnPrimary, btnGhost, Field, PageHead } from "../ui";
import { Plus, X, Upload } from "lucide-react";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const empty = {
  name: "", slug: "", status: "active", short_description: "", long_description: "",
  mrp: 0, price: 0, sale_price: null, gst_rate: 5, sku: "", images: [], colors: [],
  sizes: [], tags: [], labels: [], category_ids: [], collection_ids: [], featured: false,
  low_stock_threshold: 5, fabric: "", care: "", availability_override: null,
};

export default function ProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(empty);
  const [cats, setCats] = useState([]);
  const [cols, setCols] = useState([]);
  const [imgUrl, setImgUrl] = useState("");

  useEffect(() => {
    admin.get("/admin/categories").then((r) => setCats(r.data));
    admin.get("/admin/collections").then((r) => setCols(r.data));
    if (id) admin.get(`/admin/products/${id}`).then((r) => setP({ ...empty, ...r.data }));
  }, [id]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const toggleArr = (k, v) => setP((prev) => ({ ...prev, [k]: prev[k].includes(v) ? prev[k].filter((x) => x !== v) : [...prev[k], v] }));

  const addImage = (url) => { if (url) { set("images", [...p.images, { url, alt: p.name }]); setImgUrl(""); } };
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await admin.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } }); addImage(fileUrl(r.data.url)); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); }
  };

  const toggleSize = (s) => {
    const has = p.sizes.find((x) => x.size === s);
    set("sizes", has ? p.sizes.filter((x) => x.size !== s) : [...p.sizes, { size: s, stock: 10 }]);
  };
  const setSizeStock = (s, stock) => set("sizes", p.sizes.map((x) => (x.size === s ? { ...x, stock: Number(stock) } : x)));

  const addColor = () => set("colors", [...p.colors, { name: "Black", hex: "#111113" }]);
  const addLabel = () => set("labels", [...p.labels, { text: "NEW", variant: "cobalt" }]);

  const save = async () => {
    const body = { ...p, slug: p.slug || slugify(p.name), mrp: Number(p.mrp), price: Number(p.price),
      sale_price: p.sale_price ? Number(p.sale_price) : null };
    if (!body.name) { toast.error("Name required"); return; }
    try {
      if (id) await admin.put(`/admin/products/${id}`, body);
      else await admin.post("/admin/products", body);
      toast.success("Saved");
      navigate("/admin/products");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div>
      <PageHead title={id ? "Edit Product" : "New Product"}>
        <button onClick={() => navigate("/admin/products")} className={btnGhost}>Cancel</button>
        <button onClick={save} className={btnPrimary} data-testid="product-save-btn">Save</button>
      </PageHead>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className={`${panel} p-5 space-y-4`}>
            <Field label="Name"><input className={inputCls} value={p.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name" /></Field>
            <Field label="Slug" hint="Auto from name if blank"><input className={inputCls} value={p.slug} onChange={(e) => set("slug", e.target.value)} /></Field>
            <Field label="Short description"><input className={inputCls} value={p.short_description} onChange={(e) => set("short_description", e.target.value)} /></Field>
            <Field label="Long description"><textarea className={inputCls} rows={4} value={p.long_description} onChange={(e) => set("long_description", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fabric"><input className={inputCls} value={p.fabric} onChange={(e) => set("fabric", e.target.value)} /></Field>
              <Field label="Care"><input className={inputCls} value={p.care} onChange={(e) => set("care", e.target.value)} /></Field>
            </div>
          </div>

          <div className={`${panel} p-5 space-y-3`}>
            <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Images</p>
            <div className="flex flex-wrap gap-2">
              {p.images.map((im, i) => (
                <div key={i} className="relative w-20 h-24 bg-zinc-900">
                  <img src={im.url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => set("images", p.images.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-zinc-800 rounded-full p-0.5"><X size={12} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input className={inputCls} placeholder="Image URL" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} />
              <button onClick={() => addImage(imgUrl)} className={btnGhost}><Plus size={14} /></button>
              <label className={btnGhost + " cursor-pointer flex items-center"}><Upload size={14} /><input type="file" accept="image/*" className="hidden" onChange={upload} /></label>
            </div>
          </div>

          <div className={`${panel} p-5 space-y-3`}>
            <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Sizes & Stock</p>
            <div className="flex flex-wrap gap-2">
              {SIZES.map((s) => {
                const sel = p.sizes.find((x) => x.size === s);
                return (
                  <div key={s} className="flex flex-col items-center gap-1">
                    <button onClick={() => toggleSize(s)} className={`w-12 h-10 border text-xs ${sel ? "border-accent-ss text-accent" : "border-zinc-800 text-zinc-500"}`}>{s}</button>
                    {sel && <input type="number" value={sel.stock} onChange={(e) => setSizeStock(s, e.target.value)} className="w-12 bg-zinc-950 border border-zinc-800 px-1 py-0.5 text-xs text-center" />}
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`${panel} p-5 space-y-3`}>
            <div className="flex items-center justify-between"><p className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Colours</p><button onClick={addColor} className="text-accent text-xs"><Plus size={14} /></button></div>
            {p.colors.map((c, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input type="color" value={c.hex} onChange={(e) => set("colors", p.colors.map((x, idx) => idx === i ? { ...x, hex: e.target.value } : x))} className="w-10 h-9 bg-transparent" />
                <input className={inputCls} value={c.name} onChange={(e) => set("colors", p.colors.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} />
                <button onClick={() => set("colors", p.colors.filter((_, idx) => idx !== i))}><X size={16} className="text-zinc-500" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className={`${panel} p-5 space-y-4`}>
            <Field label="Status">
              <select className={inputCls} value={p.status} onChange={(e) => set("status", e.target.value)}>
                <option value="active">Active</option><option value="draft">Draft</option><option value="hidden">Hidden</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={p.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on homepage</label>
            <div className="grid grid-cols-2 gap-3">
              <Field label="MRP"><input type="number" className={inputCls} value={p.mrp} onChange={(e) => set("mrp", e.target.value)} data-testid="product-mrp" /></Field>
              <Field label="Selling price"><input type="number" className={inputCls} value={p.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sale price"><input type="number" className={inputCls} value={p.sale_price || ""} onChange={(e) => set("sale_price", e.target.value)} /></Field>
              <Field label="GST %"><input type="number" className={inputCls} value={p.gst_rate} onChange={(e) => set("gst_rate", Number(e.target.value))} /></Field>
            </div>
            <Field label="SKU"><input className={inputCls} value={p.sku} onChange={(e) => set("sku", e.target.value)} /></Field>
            <Field label="Tags (comma)"><input className={inputCls} value={(p.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></Field>
          </div>

          <div className={`${panel} p-5 space-y-2`}>
            <div className="flex items-center justify-between"><p className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Promo Labels</p><button onClick={addLabel} className="text-accent"><Plus size={14} /></button></div>
            {p.labels.map((l, i) => (
              <div key={i} className="flex gap-2">
                <input className={inputCls} value={l.text} onChange={(e) => set("labels", p.labels.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x))} />
                <button onClick={() => set("labels", p.labels.filter((_, idx) => idx !== i))}><X size={16} className="text-zinc-500" /></button>
              </div>
            ))}
          </div>

          <div className={`${panel} p-5`}>
            <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold mb-2">Categories</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {cats.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={p.category_ids.includes(c.id)} onChange={() => toggleArr("category_ids", c.id)} /> {c.parent_id ? "— " : ""}{c.name}</label>
              ))}
            </div>
          </div>

          <div className={`${panel} p-5`}>
            <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold mb-2">Collections</p>
            <div className="space-y-1">
              {cols.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={p.collection_ids.includes(c.id)} onChange={() => toggleArr("collection_ids", c.id)} /> {c.name}</label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
